/**
 * Import notițe dintr-un ZIP markdown (reverse al exportNotesToZip).
 *
 * Flux:
 *   1. Parse ZIP cu JSZip, enumerăm fișierele `.md` (ignorăm `_assets/`)
 *   2. Pentru fiecare: extract front-matter opțional (title, slug), convert
 *      markdown → HTML cu `marked`
 *   3. Reconstruim ierarhia folderelor. Dacă un folder n-are `.md` cu același
 *      nume la același nivel, creăm o notiță wrapper goală cu acel titlu.
 *   4. BFS după depth: creăm rădăcinile → subnotițele, păstrând map
 *      (zipPath → noteId) pentru parent lookup.
 *   5. Fiecare notiță e criptată client-side cu DEK nou și POST /notes.
 *
 * Backend-ul face dedupe automat pe slug când detectează coliziune.
 */
import JSZip from 'jszip';
import { marked } from 'marked';

import { notesApi, type NoteDetail } from '@/api/notes';
import {
  bytesToBase64,
  encryptSym,
  generateDEK,
  strToBytes,
  wrapKey,
  type KeyPair,
} from '@/crypto/sodium';
import libsodium from 'libsodium-wrappers-sumo';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import i18n from '@/i18n';

export interface ImportProgress {
  total: number;
  done: number;
  currentLabel: string;
}

export interface ImportResult {
  created: number;
  failed: Array<{ path: string; error: string }>;
}

export interface ImportOptions {
  tenantId: string;
  keyPair: KeyPair;
  onProgress?: (p: ImportProgress) => void;
}

interface ParsedEntry {
  /** Path relativ la root-ul logic din ZIP (fără folderul tenant). */
  relPath: string;
  /** Segmente fără `.md` final (folderii + titlul fișierului). */
  segments: string[];
  title: string;
  html: string;
  isWrapper: boolean;
}

// ============================================================================
// Parse ZIP → listă entries cu ierarhia dedusă
// ============================================================================

async function parseZip(file: File): Promise<ParsedEntry[]> {
  const zip = await JSZip.loadAsync(file);

  // Determine root: dacă TOATE entries-urile încep cu același prefix dintr-un
  // singur segment, îl tratăm ca folder tenant și-l scoatem. Altfel treat
  // ZIP-ul ca fiind flat la root.
  const allPaths = Object.keys(zip.files).filter((p) => !zip.files[p].dir);
  if (allPaths.length === 0) return [];

  const rootPrefix = detectRootPrefix(allPaths);

  const mdEntries: Array<{ zipPath: string; relPath: string }> = [];
  for (const zipPath of allPaths) {
    const stripped = rootPrefix
      ? zipPath.slice(rootPrefix.length + 1)
      : zipPath;
    if (!stripped) continue;
    if (stripped.startsWith('_assets/') || stripped.startsWith('_assets\\')) {
      continue;
    }
    if (!stripped.toLowerCase().endsWith('.md')) continue;
    mdEntries.push({ zipPath, relPath: stripped });
  }

  const parsed: ParsedEntry[] = [];
  const seenSegments = new Set<string>();

  for (const { zipPath, relPath } of mdEntries) {
    const raw = await zip.files[zipPath].async('string');
    const { title: fmTitle, body } = extractFrontMatter(raw);

    // Segmente = path fără `.md` final
    const noMdPath = relPath.replace(/\.md$/i, '');
    const segments = noMdPath.split('/').filter(Boolean);
    const lastSeg = segments[segments.length - 1];
    const title = fmTitle || lastSeg || i18n.t('notes_ui.import.untitled');

    const html = markdownToHtml(body);

    parsed.push({
      relPath,
      segments,
      title,
      html,
      isWrapper: false,
    });
    seenSegments.add(segments.join('/'));
  }

  // Creăm wrappers pentru foldere fără `.md` corespondent. Ex: avem
  // `A/B.md` dar nu avem `A.md` la același nivel → creăm un wrapper `A`
  // ca notiță părinte.
  const wrapperSet = new Set<string>();
  for (const entry of parsed) {
    for (let i = 1; i < entry.segments.length; i++) {
      const parentSegs = entry.segments.slice(0, i);
      const key = parentSegs.join('/');
      if (!seenSegments.has(key) && !wrapperSet.has(key)) {
        wrapperSet.add(key);
        parsed.push({
          relPath: key + '.md',
          segments: parentSegs,
          title: parentSegs[parentSegs.length - 1],
          html: '<p></p>',
          isWrapper: true,
        });
      }
    }
  }

  // BFS: sortăm după depth ca părinții să fie creați înaintea copiilor
  parsed.sort((a, b) => {
    if (a.segments.length !== b.segments.length) {
      return a.segments.length - b.segments.length;
    }
    return a.relPath.localeCompare(b.relPath);
  });

  return parsed;
}

function detectRootPrefix(paths: string[]): string {
  if (paths.length === 0) return '';
  const first = paths[0].split('/')[0];
  if (!first) return '';
  for (const p of paths) {
    if (!p.startsWith(first + '/') && p !== first) return '';
  }
  return first;
}

// ============================================================================
// Front-matter minimal (`---\ntitle: "Foo"\n---`)
// ============================================================================

function extractFrontMatter(raw: string): { title: string | null; body: string } {
  const match = raw.match(/^---\r?\n([\s\S]*?)\r?\n---\r?\n?/);
  if (!match) return { title: null, body: raw };
  const body = raw.slice(match[0].length);
  const block = match[1];
  let title: string | null = null;
  for (const line of block.split(/\r?\n/)) {
    const m = line.match(/^\s*title\s*:\s*(.+)$/);
    if (m) {
      const val = m[1].trim();
      try {
        title = JSON.parse(val);
      } catch {
        title = val.replace(/^['"]|['"]$/g, '');
      }
      break;
    }
  }
  return { title, body };
}

// ============================================================================
// Markdown → HTML
// ============================================================================

function markdownToHtml(md: string): string {
  const result = marked.parse(md, { async: false, breaks: false, gfm: true });
  // `marked` lasă HTML-ul brut din .md să treacă — îl sanitizăm înainte de salvare.
  return typeof result === 'string' ? sanitizeHtml(result) : '';
}

// ============================================================================
// Import main
// ============================================================================

export async function importNotesFromZip(
  file: File,
  opts: ImportOptions,
): Promise<ImportResult> {
  const { tenantId, keyPair, onProgress } = opts;

  const entries = await parseZip(file);
  const total = entries.length;
  const result: ImportResult = { created: 0, failed: [] };

  if (total === 0) return result;

  // Map "segments joined" → note ID creat, pentru parent lookup.
  const createdBySegments = new Map<string, string>();

  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    onProgress?.({ total, done: i, currentLabel: entry.title });

    try {
      const parentKey = entry.segments.slice(0, -1).join('/');
      const parentId = parentKey ? createdBySegments.get(parentKey) ?? null : null;

      const detail = await createNote({
        tenantId,
        keyPair,
        title: entry.title,
        html: entry.html,
        parentId,
      });
      createdBySegments.set(entry.segments.join('/'), detail.id);
      result.created += 1;
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      result.failed.push({ path: entry.relPath, error: msg });
    }
  }

  onProgress?.({ total, done: total, currentLabel: i18n.t('notes_ui.import.done') });
  return result;
}

async function createNote(args: {
  tenantId: string;
  keyPair: KeyPair;
  title: string;
  html: string;
  parentId: string | null;
}): Promise<NoteDetail> {
  const dek = await generateDEK();
  try {
    const plain = strToBytes(args.html);
    const enc = await encryptSym(plain, dek);
    const wrapped = await wrapKey(dek, args.keyPair.publicKey);
    return await notesApi.create(args.tenantId, {
      title: args.title.slice(0, 256),
      parent_id: args.parentId,
      ciphertext: bytesToBase64(enc.ciphertext),
      nonce: bytesToBase64(enc.nonce),
      dek_wrapped: bytesToBase64(wrapped),
    });
  } finally {
    libsodium.memzero(dek);
  }
}
